import {Component, DestroyRef, EventEmitter, inject, OnInit, Output} from '@angular/core';
import {ControlContainer, FormGroup, FormGroupDirective, ReactiveFormsModule, Validators} from '@angular/forms';

import { AsyncPipe, CommonModule } from "@angular/common";
import {TranslateModule, TranslatePipe, TranslateService} from "@ngx-translate/core";
import {
  enumToIbpRadioOption,
  IbpRadioButtonComponent,
  IbpRadioOption,
} from "../../../ibp-components/third-party/ibp-prime-ng/ibp-radio-button/ibp-radio-button.component";
import {VatMethod2} from "../../../enums/vat-method";
import {
  enumToIbpSelectOptions,
  IbpSelectComponent,
  IbpSelectOption,
} from "../../../ibp-components/third-party/ibp-prime-ng/ibp-select/ibp-select.component";
import {
  CHChartOfAccountsTypes,
  DEChartOfAccountsTypes
} from "../../../models/chart-accounts/chart-of-accounts-type.enum";
import {Observable} from "rxjs";
import {filter, map, switchMap, tap} from "rxjs/operators";
import {ClientCreationService} from "../services/client-creation.service";
import {UserService} from "../../../service/user.service";
import {UserProfile} from "../../../models/user/userProfile";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {LocalesService} from "../../../service/locales.service";
import {GeneralDataService} from '../../../service/general-data.service';
import {IbpTooltipDirective} from "../../../ibp-directives/ibp-tooltip/ibp-tooltip.directive";
import {IbpVatAutoFormatDirective} from "../../../ibp-directives/ibp-vat-formatter/vat-auto-format.directive";
import {RmaValidators} from "../../../shared/rma-validators";


import {IbpButtonComponent} from '../../../ibp-components/third-party/ibp-prime-ng/ibp-button/ibp-button.component';
import {
  IbpCheckboxComponent
} from '../../../ibp-components/third-party/ibp-prime-ng/ibp-checkbox/ibp-checkbox.component';
import {
  IbpCalendarComponent
} from '../../../ibp-components/third-party/ibp-prime-ng/ibp-calendar/ibp-calendar.component';
import {
  IbpTextInputComponent
} from '../../../ibp-components/third-party/ibp-prime-ng/ibp-text-input/ibp-text-input.component';
import {IbpFormHeaderComponent} from "../../../ibp-components/ibp-form-header/ibp-form-header.component";
import {
  IbpInputTextareaComponent
} from "../../../ibp-components/third-party/ibp-prime-ng/ibp-input-textarea/ibp-input-textarea.component";

@Component({
  selector: 'ibp-accounting-info-step',
  templateUrl: './accounting-info-step.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    TranslatePipe,
    IbpTooltipDirective,
    IbpVatAutoFormatDirective,
    CommonModule,
    TranslateModule,
    IbpButtonComponent,
    IbpSelectComponent,
    IbpCheckboxComponent,
    IbpCalendarComponent,
    IbpTextInputComponent,
    IbpFormHeaderComponent,
    IbpRadioButtonComponent,
    IbpInputTextareaComponent,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective
    }],
})
export class AccountingInfoStepComponent implements OnInit{
  private rootFormGroup: FormGroupDirective = inject(FormGroupDirective);
  private translate: TranslateService = inject(TranslateService);
  private localesService: LocalesService = inject(LocalesService);
  private clientCreationService: ClientCreationService = inject(ClientCreationService);
  private userService: UserService = inject(UserService);
  private generalDataService: GeneralDataService = inject(GeneralDataService);
  private destroy$ = inject(DestroyRef);
  @Output() prevCallback = new EventEmitter();
  @Output() nextCallback = new EventEmitter();
  public form: FormGroup;

  public vatMethodRadioOptions: IbpRadioOption[];
  public chartOfAccountTypeOptions: IbpSelectOption[];
  public languageOptions$: Observable<IbpSelectOption[]>;
  public currencyOptions$: Observable<IbpSelectOption[]>;
  protected users$: Observable <IbpSelectOption[]> = this.clientCreationService.getCompanyInfoData()
    .pipe(
      map(value => value?.value?.companyInfo?.domain),
      filter(value => value !== null && value !== undefined && value !== ''),
      switchMap(value => this.userService.getUsersByDomainId(value)),
      map((users: UserProfile[]) => users.map(user => {
          return {
            label: user.fullName,
            value: user.id
          }
        })
      ),
    )

  protected selectedCountry$: Observable<string> = this.clientCreationService.getCompanyInfoData()
    .pipe(
      map(value => value?.value?.companyInfo?.country),
      filter(value => value !== null && value !== undefined && value !== '')
    )

  public ngOnInit() {
    this.form = this.rootFormGroup.control;

    this.setupAccountingPeriodValidator();
    this.checkVatLiableChange();

    this.vatMethodRadioOptions = enumToIbpRadioOption(this.translate, VatMethod2, "COMPANY_PROPERTIES.FORM.VAT_METHOD.", false, null, true)
      .filter(option => option.value !== VatMethod2.NONE);

    const allowedAccountType = this.form.get('companyInfo.country')?.value == 'CH'
      ? CHChartOfAccountsTypes : DEChartOfAccountsTypes;

    this.chartOfAccountTypeOptions = enumToIbpSelectOptions(this.translate, allowedAccountType, "COMPANY_PROPERTIES.FORM.CHART_OF_ACCOUNT_TYPE_OPTIONS.");
    this.languageOptions$ = this.loadLanguageOptions();
    this.currencyOptions$ = this.loadCurrencyOptions();
    this.form.get('accountingInfo.chartOfAccountType').setValue(this.chartOfAccountTypeOptions[0].value);
  }

  private setupAccountingPeriodValidator() {
    this.form.get("accountingInfo.entryDate").valueChanges.pipe(
      takeUntilDestroyed(this.destroy$),
    ).subscribe(entryDate => {
      const exitDateControl = this.form.get("accountingInfo.exitDate");
      const validators = entryDate != null ? [Validators.required, RmaValidators.dateAfter(entryDate)] : [Validators.required];

      exitDateControl.setValidators(validators);
      exitDateControl.updateValueAndValidity();
    })
  }

  private checkVatLiableChange() {
    this.form.get('accountingInfo.vatLiable').valueChanges
      .pipe(
        tap(value => {
          this.updateVatMethod(value)
        }),
        takeUntilDestroyed(this.destroy$)
      )
      .subscribe()
  }

  private updateVatMethod(isVatLiable: boolean) {
    if(!isVatLiable) {
      this.form.get('accountingInfo.vatMethod').patchValue(VatMethod2.NONE);
    }
  }

  private loadLanguageOptions() {
    return this.localesService.getSupportedLocales()
      .pipe(
        map(locales =>
          locales.map((locale) => ({ label: locale.name, value: locale.rmaCode }))
        )
      )
  }

  private loadCurrencyOptions(): Observable<IbpSelectOption[]> {
    return this.generalDataService.getCurrencies().pipe(
      map(currencies => currencies.map(currency => ({label: currency.name, value: currency.code})))
    );
  }

  public isFormDisabledOrValid() {
    return this.form.get('accountingInfo').disabled || this.form.get('accountingInfo').valid;
  }

  public onNextClick() {
    this.clientCreationService.setAccountInfoData(this.form);
    this.nextCallback.emit()
  }
}
